import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  residentApi,
  type CreateResidentPayload,
  type UpdateResidentPayload,
} from '../api/residentApi';
import type { ResidentStatus } from '../components';

// Extended query keys for residents
// const residentQueryKeys = {
//   all: ['residents'] as const,
//   lists: () => [...residentQueryKeys.all, 'list'] as const,
//   list: (params?: any) => [...residentQueryKeys.lists(), params] as const,
//   details: () => [...residentQueryKeys.all, 'detail'] as const,
//   detail: (id: string) => [...residentQueryKeys.details(), id] as const,
//   stats: (id: string) => [...residentQueryKeys.detail(id), 'stats'] as const,
// };

// // Hook to get residents with caching and error handling
// export const useResidents = (params?: {
//   page?: number
//   limit?: number
//   search?: string
//   houseId?: string
//   isActive?: boolean
// }) => {
//   return useQuery({
//     queryKey: residentQueryKeys.list(params),
//     queryFn: () => residentApi.getResidents(params),
//     staleTime: 2 * 60 * 1000, // 2 minutes
//     retry: (failureCount, error: any) => {
//       if (error?.response?.status >= 400 && error?.response?.status < 500) {
//         if (![408, 429].includes(error.response.status)) {
//           return false
//         }
//       }
//       return failureCount < 3
//     },
//     retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
//     // onError removed - use error handling in components instead
//   })
// }

// // Hook to get single resident with error handling
// export const useResident = (id: string) => {
//   return useQuery({
//     queryKey: residentQueryKeys.detail(id),
//     queryFn: () => residentApi.getResidentById(id),
//     enabled: !!id,
//     staleTime: 5 * 60 * 1000, // 5 minutes
//     retry: (failureCount, error: any) => {
//       if (error?.response?.status >= 400 && error?.response?.status < 500) {
//         if (![408, 429].includes(error.response.status)) {
//           return false
//         }
//       }
//       return failureCount < 3
//     },
//     retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
//     // onError removed - use error handling in components instead
//   })
// }

// // Hook to get resident stats
// export const useResidentStats = (id: string) => {
//   return useQuery({
//     queryKey: residentQueryKeys.stats(id),
//     queryFn: () => residentApi.getResidentStats(id),
//     enabled: !!id,
//   })
// }

// // Hook to get archived residents
// export const useArchivedResidents = (params?: {
//   page?: number
//   limit?: number
//   search?: string
//   houseId?: string
// }) => {
//   return useQuery({
//     queryKey: [...residentQueryKeys.lists(), 'archived', params],
//     queryFn: () => residentApi.getArchivedResidents(params),
//   })
// }

// // Create resident mutation with retry logic
// export const useCreateResident = () => {
//   const queryClient = useQueryClient()

//   return useMutation({
//     mutationFn: (data: CreateResidentData) => {
//       const retryHandler = createRetryHandler(3, 1000)
//       return retryHandler(() => residentApi.createResident(data))
//     },
//     onSuccess: (_response, variables) => {
//       toast.success(`Resident "${variables.firstName} ${variables.lastName}" created successfully!`)
//       queryClient.invalidateQueries({ queryKey: residentQueryKeys.all })
//     },
//     onError: (error: any, _variables) => {
//       showApiError(error, 'Failed to create resident')
//       throw error
//     },
//   })
// }

// // Update resident mutation
// export const useUpdateResident = () => {
//   const queryClient = useQueryClient()

//   return useMutation({
//     mutationFn: ({ id, data }: { id: string; data: Partial<CreateResidentData> }) =>
//       residentApi.updateResident(id, data),
//     onMutate: async ({ id, data }) => {
//       await queryClient.cancelQueries({ queryKey: residentQueryKeys.detail(id) })
//       await queryClient.cancelQueries({ queryKey: residentQueryKeys.all })

//       const previousResident = queryClient.getQueryData(residentQueryKeys.detail(id))

//       // Optimistically update single resident
//       queryClient.setQueryData(residentQueryKeys.detail(id), (old: Resident | undefined) =>
//         old ? { ...old, ...data, updatedAt: new Date().toISOString() } : old
//       )

//       // Update in lists as well
//       const updateResidentsQueryKey = [...residentQueryKeys.list()]
//       optimisticUtils.updateInList(queryClient, updateResidentsQueryKey, id, data as any)

//       return { previousResident }
//     },
//     onSuccess: (response, { id }) => {
//       toast.success('Resident updated successfully!')
//       queryClient.setQueryData(residentQueryKeys.detail(id), response)
//       queryClient.invalidateQueries({ queryKey: residentQueryKeys.all })
//     },
//     onError: (error: any, { id }, context) => {
//       if (context?.previousResident) {
//         queryClient.setQueryData(residentQueryKeys.detail(id), context.previousResident)
//       }
//       showApiError(error, 'Failed to update resident')
//       throw error
//     },
//   })
// }

// // Move out resident mutation
// export const useMoveOutResident = () => {
//   const queryClient = useQueryClient()

//   return useMutation({
//     mutationFn: ({ id, data }: { id: string; data: { moveOutDate: string; notes?: string } }) =>
//       residentApi.moveOutResident(id, data),
//     onSuccess: (response, { id }) => {
//       toast.success('Resident moved out successfully!')
//       queryClient.setQueryData(residentQueryKeys.detail(id), response)
//       queryClient.invalidateQueries({ queryKey: residentQueryKeys.all })
//     },
//     onError: (error: any) => {
//       showApiError(error, 'Failed to move out resident')
//       throw error
//     },
//   })
// }

// // Restore resident mutation
// export const useRestoreResident = () => {
//   const queryClient = useQueryClient()

//   return useMutation({
//     mutationFn: (id: string) => residentApi.restoreResident(id),
//     onSuccess: (response, id) => {
//       toast.success('Resident restored successfully!')
//       queryClient.setQueryData(residentQueryKeys.detail(id), response)
//       queryClient.invalidateQueries({ queryKey: residentQueryKeys.all })
//     },
//     onError: (error: any) => {
//       showApiError(error, 'Failed to restore resident')
//       throw error
//     },
//   })
// }

/**
 * Resident Management Mutatioons
 */

export const useCreateResident = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateResidentPayload) =>
      residentApi.createResident(payload),

    onSuccess: (data) => {
      console.log('Resident created successfully:', data.user.name);

      // ✅ Invalidate all residents queries (including filter-based ones)
      // This will invalidate ['residents'], ['residents', filters], etc.
      queryClient.invalidateQueries({ queryKey: ['residents'] });
    },

    onError: (error: any) => {
      console.error('Error creating resident:', error.message);
    },
  });
};

export const useDeleteResident = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (residentId: string) => residentApi.deleteResident(residentId),

    onSuccess: (response) => {
      console.log('✅ Resident deleted:', response);

      // ✅ Invalidate all residents queries (including filter-based ones)
      queryClient.invalidateQueries({ queryKey: ['residents'] });

      // ✅ Also invalidate archived residents in case the deleted resident was archived
      queryClient.invalidateQueries({ queryKey: ['archived-residents'] });
    },

    onError: (error: any) => {
      console.error(
        '❌ Failed to delete resident:',
        error.response?.data?.message || error.message,
      );
    },
  });
};

export const useUpdateResidentStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      residentId,
      status,
    }: {
      residentId: string;
      status: ResidentStatus;
    }) => residentApi.updateResidentStatus(residentId, { status }),

    onSuccess: (_, variables) => {
      // ✅ Invalidate the specific resident details query
      queryClient.invalidateQueries({
        queryKey: ['resident', variables.residentId],
      });

      // ✅ Invalidate all residents queries (including filter-based ones)
      queryClient.invalidateQueries({ queryKey: ['residents'] });

      // ✅ Invalidate archived residents (status change might move resident between lists)
      queryClient.invalidateQueries({ queryKey: ['archived-residents'] });
    },
    onError: (error: any) => {
      console.log(error?.response?.data?.message || 'Failed to update status');
    },
  });
};

export const useUpdateResident = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    // ✅ Correct Partial syntax and proper function call
    mutationFn: (payload: Partial<UpdateResidentPayload>) =>
      residentApi.updateResident({ residentId: id, payload }),

    onSuccess: (data) => {
      console.log('Resident updated successfully:', data.fullname);

      // ✅ Invalidate the specific resident details query
      queryClient.invalidateQueries({ queryKey: ['resident', id] });

      // ✅ Invalidate all residents queries (including filter-based ones)
      // This will automatically invalidate ['residents'], ['residents', filters], etc.
      queryClient.invalidateQueries({ queryKey: ['residents'] });
    },

    onError: (error: any) => {
      console.error('Error updating resident:', error.message);
    },
  });
};
